import {
  Button,
  CheckboxFormField,
  DatePickerFormField,
  Divider,
  Form,
  IDatePickerPayload,
  IFormRender,
  Justify,
  Label,
  Loader,
  Row,
  TextFormField,
} from '@busybusy/webapp-react-ui';
import FlexContainer from '@busybusy/webapp-react-ui/dist/components/FlexContainer/FlexContainer';
import { EquipmentModelCreateInput, EquipmentModelType, OrganizationCustomFieldsTypes } from '__generated__/graphql';
import classNames from 'classnames';
import ProfileImageFormField from 'components/domain/member/ProfileImageFormField/ProfileImageFormField';
import DetailHeaderWithText from 'components/foundation/DetailHeaderWithText/DetailHeaderWithText';
import CurrencyFormField from 'components/foundation/form-fields/CurrencyFormField/CurrencyFormField';
import FormAccordion from 'components/foundation/FormAccordion/FormAccordion';
import useEquipmentChecklistRoutines from 'containers/equipment-inspections/hooks/useEquipmentChecklistRoutines';
import useEquipmentImageUpdate from 'containers/manage-employees/hooks/useEquipmentImageUpdate';
import useEquipmentModels from 'containers/manage-euipment/hooks/useEquipmentModels';
import { useActiveMember, useEquipment, useEquipmentHours, useOpenable } from 'hooks';
import useCreateEquipmentCostHistory from 'hooks/models/equipment-cost-history/useCreateEquipmentCostHistory';
import useOrganizationCustomFields from 'hooks/models/organization-custom-fields/useOrganizationCustomFields';
import useOnMount from 'hooks/utils/useOnMount/useOnMount';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import React, { useRef, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { isoTimeStampUtc } from 'utils/dateUtils';
import { useFeatureFlags } from 'utils/features';
import { convertStringToSafeNumber } from 'utils/numberUtils';
import { extractCustomFields, ICustomFieldTableRow } from 'utils/organizationCustomFieldsUtils';
import { isAdmin } from 'utils/positionUtils';
import { v_number_decimals_only, v_require } from 'utils/validations';
import EquipmentCategoryFormField from '../EquipmentCategoryFormField/EquipmentCategoryFormField';
import EquipmentInspectionChecklistPickerFormField from '../EquipmentInspectionChecklistPickerFormField/EquipmentInspectionChecklistPickerFormField';
import EquipmentMakeFormField from '../EquipmentMakeFormField/EquipmentMakeFormField';
import EquipmentModelFormField from '../EquipmentModelFormField/EquipmentModelFormField';
import { IExistingModel } from '../EquipmentModelPicker/EquipmentModelPicker';
import EquipmentTypeFormField from '../EquipmentTypeFormField/EquipmentTypeFormField';
import EquipmentTypesDialog from '../EquipmentTypesDialog/EquipmentTypesDialog';
import ManageEquipmentCategoriesDialog from '../ManageEquipmentCategoriesDialog/ManageEquipmentCategoriesDialog';
import ManageEquipmentMakesDialog from '../ManageEquipmentMakesDialog/ManageEquipmentMakesDialog';
import './EquipmentCreateForm.scss';

export interface IEquipmentCreateFormProps {
  className?: ClassName;
  onSubmit: (data?: string) => void;
}

export interface IEquipmentCreateFormData {
  equipmentName?: string;
  type?: EquipmentModelType;
  categoryId?: string;
  makeId?: string;
  modelId?: string;
  modelTitle?: string;
  year?: number;
  trackManualHours?: boolean;
  image?: { file: File | null };
  imageUrl?: string;
  hourMeter?: number | undefined;
  hourlyCost?: number | undefined;
  hourlyCostEffectiveDate?: IDatePickerPayload | undefined;
  customField1?: string | null;
  customField2?: string | null;
  customField3?: string | null;
  customField4?: string | null;
  customField5?: string | null;
  preOperationChecklistId?: string | null;
  postOperationChecklistId?: string | null;
  routineInspectionChecklistId1?: string | null;
  routineInspectionChecklistId2?: string | null;
  routineInspectionChecklistId3?: string | null;
  routineInspectionInterval1?: string | null;
  routineInspectionInterval2?: string | null;
  routineInspectionInterval3?: string | null;
}

const EquipmentCreateForm = (props: IEquipmentCreateFormProps) => {
  const { className, onSubmit } = props;

  const today = DateTime.local().startOf('day');

  const { createEquipment } = useEquipment();
  const { createEquipmentHours } = useEquipmentHours();
  const { createEquipmentModel } = useEquipmentModels();
  const createEquipmentCostHistory = useCreateEquipmentCostHistory();
  const imageUpdate = useEquipmentImageUpdate();
  const { getOrganizationCustomFields } = useOrganizationCustomFields();
  const activeMember = useActiveMember();
  const isEquipmentInspectionsEnabled = useFeatureFlags('EQUIPMENT_INSPECTIONS');
  const { createEquipmentChecklistRoutine } = useEquipmentChecklistRoutines();

  const [reloadCategories, setReloadCategories] = useState(false);
  const [modelToCreate, setModelToCreate] = useState<{ id: string | null; name: string | null }>();
  const [customFields, setCustomFields] = useState<ICustomFieldTableRow[]>([]);

  const dateOpenable = useOpenable();
  const loaderOpenable = useOpenable();
  const errorToastState = useOpenable();
  const manageCategoriesOpenable = useOpenable();
  const manageMakesOpenable = useOpenable();
  const typesDialogOpenable = useOpenable();

  useOnMount(() => {
    getCustomFields();
  });

  const getCustomFields = async () => {
    const organizationCustomFields = await getOrganizationCustomFields(OrganizationCustomFieldsTypes.Equipment);
    if (organizationCustomFields) {
      const extractedFields = extractCustomFields(organizationCustomFields);
      setCustomFields(extractedFields);
    }
  };

  const errorToastMessage = useRef(t('There was an unexpected error.'));

  const onSubmitForm = (data: IEquipmentCreateFormData) => {
    loaderOpenable.open();

    createNewEquipment(data);
  };

  const handleCategoriesDialogClose = () => {
    manageCategoriesOpenable.close();
    setReloadCategories((prev) => !prev);
  };

  const createNewEquipment = async (data: IEquipmentCreateFormData) => {
    if (!isNil(modelToCreate)) {
      const input: EquipmentModelCreateInput = {
        createdOn: isoTimeStampUtc(),
        equipmentCategoryId: data.categoryId!,
        equipmentMakeId: data.makeId!,
        title: modelToCreate.name!,
        type: data.type!,
        id: modelToCreate.id,
      };
      await createEquipmentModel(input);
    }

    createEquipment(
      data.equipmentName!,
      data.modelId!,
      data.trackManualHours ?? false,
      data.year,
      data.customField1,
      data.customField2,
      data.customField3,
      data.customField4,
      data.customField5,
      data.preOperationChecklistId,
      data.postOperationChecklistId
    ).then(async (results) => {
      const equipmentId = results.data.createEquipment.id;
      const hasRoutineInspections =
        data.routineInspectionChecklistId1 || data.routineInspectionChecklistId2 || data.routineInspectionChecklistId3;
      if (data.image) {
        {
          await imageUpdate(equipmentId, data.image.file).catch(() => {
            errorToastMessage.current = t('There was an error saving the image.');
            errorToastState.open();
            loaderOpenable.close();
          });
        }
      }
      let shouldSubmit = true;

      if (data.hourMeter && data.hourMeter) {
        shouldSubmit = false;
        createEquipmentHours(equipmentId, data.hourMeter)
          .then(() => {
            shouldSubmit = true;
          })
          .catch(() => {
            errorToastMessage.current = t('There was an error saving the hour meter.');
            errorToastState.open();
          });
      }

      if (data.hourlyCost && data.hourlyCost > 0) {
        if (!data.hourlyCostEffectiveDate) {
          errorToastMessage.current = t('Effective date required');
          errorToastState.open();
          loaderOpenable.close();
        } else {
          shouldSubmit = false;
          createEquipmentCostHistory(
            equipmentId,
            undefined,
            data.hourlyCost,
            data.hourlyCostEffectiveDate?.value
              ? DateTime.fromJSDate(data.hourlyCostEffectiveDate.value).startOf('day').toISO()
              : today.toISO()
          )
            .then(() => {
              onSubmit(results.data.createEquipment.id);
            })
            .catch(() => {
              errorToastMessage.current = t('There was an error saving cost history.');
              errorToastState.open();
            });
        }
      }

      if (hasRoutineInspections) {
        const routineItems = [
          { id: data.routineInspectionChecklistId1, interval: data.routineInspectionInterval1 },
          { id: data.routineInspectionChecklistId2, interval: data.routineInspectionInterval2 },
          { id: data.routineInspectionChecklistId3, interval: data.routineInspectionInterval3 },
        ].filter((item) => item.id);

        for (const item of routineItems) {
          await createEquipmentChecklistRoutine({
            equipmentId: results.data.createEquipment.id,
            equipmentInspectionChecklistId: item.id!,
            createdOn: isoTimeStampUtc(),
            recurringDays: parseInt(item.interval!),
          });
        }
      }

      if (shouldSubmit) {
        onSubmit(results.data.createEquipment.id);
      }

      loaderOpenable.close();
    });
  };

  const handleMakeOrCategoryChange = (form: IFormRender<IEquipmentCreateFormData>) => {
    form.setFormData({ ...form.state.data, modelId: undefined });
  };

  const handleExistingModelSelected = (model: IExistingModel, form: IFormRender<IEquipmentCreateFormData>) => {
    form.setFormData({
      ...form.state.data,
      makeId: model.makeId,
      categoryId: model.categoryId,
    });
  };

  const handleModelChange = (value: string | null, form: IFormRender<IEquipmentCreateFormData>) => {
    // clear the make and category if the model is set to null.
    if (isNil(value)) {
      form.setFormData({
        ...form.state.data,
        makeId: undefined,
        categoryId: undefined,
      });
    }
  };

  const classes = classNames('create-equipment-form mb-6 px-8', className);

  const CustomFormField = (
    form: IFormRender<IEquipmentCreateFormData>,
    field: ICustomFieldTableRow,
    index: number,
    isAdmin: boolean
  ) => {
    const { title, subtitle, adminOnly } = field;
    const fieldName = `customField${index + 1}`;

    if (!title?.trim() || (adminOnly && !isAdmin)) {
      return null;
    }
    return (
      <div key={fieldName}>
        <Label>{title}</Label>
        <TextFormField name={fieldName} form={form} />
        {subtitle && <div className="text-sm text-gray-500">{subtitle}</div>}
      </div>
    );
  };

  const renderFormFields = ({ ...form }: IFormRender<IEquipmentCreateFormData>) => (
    <div>
      <Label>{t('Name')}</Label>
      <TextFormField name={'equipmentName'} form={form} validations={[{ validation: v_require }]} autofocus={true} />

      <Label>{t('Type')}</Label>
      <span className="types-help-text">
        {t('To learn more about types, ')}
        <span className="types-link" onClick={typesDialogOpenable.open}>
          {t('click here.')}
        </span>
      </span>

      <EquipmentTypeFormField
        name={'type'}
        form={form}
        validations={[{ validation: v_require }]}
        onChange={(value) => {
          if (value !== EquipmentModelType.Equipment) {
            form.setFormData({ ...form.state.data, trackManualHours: false });
          }
        }}
      />

      <FlexContainer justifyContent={'space-between'}>
        <Label className={'pt-2'}>{t('Category')}</Label>
        <Button type="link" className={'p-0 manage-button'} onClick={manageCategoriesOpenable.open}>
          {t('Manage')}
        </Button>
      </FlexContainer>
      <EquipmentCategoryFormField
        name={'categoryId'}
        form={form}
        validations={[{ validation: v_require }]}
        disabled={isNil(form.state.data.type)}
        onChange={() => handleMakeOrCategoryChange(form)}
        reload={reloadCategories}
      />

      <FlexContainer justifyContent={'space-between'}>
        <Label className={'pt-2'}>{t('Make')}</Label>
        <Button type="link" className={'p-0 manage-button'} onClick={manageMakesOpenable.open}>
          {t('Manage')}
        </Button>
      </FlexContainer>
      <EquipmentMakeFormField
        name={'makeId'}
        equipmentCategoryId={form.state.data.categoryId}
        form={form}
        onChange={() => handleMakeOrCategoryChange(form)}
        disabled={isNil(form.state.data.type)}
        validations={[{ validation: v_require }]}
      />

      <Label>{t('Model')}</Label>
      <EquipmentModelFormField
        name={'modelId'}
        equipmentMakeId={form.state.data.makeId}
        equipmentCategoryId={form.state.data.categoryId}
        equipmentModelType={form.state.data.type}
        form={form}
        onChange={(value) => handleModelChange(value, form)}
        onExistingModel={(model) => {
          handleExistingModelSelected(model, form);
        }}
        onNewModel={setModelToCreate}
        disabled={isNil(form.state.data.type)}
        validations={[{ validation: v_require }]}
      />

      <Label optional={true}>{t('Year')}</Label>
      <TextFormField name="year" className={'year-field'} form={form} />

      <Label optional={true}>{t('Equipment Image')}</Label>
      <ProfileImageFormField name="image" initialImage={null} form={form} />

      {form.state.data.type === EquipmentModelType.Equipment && (
        <>
          <CheckboxFormField name="trackManualHours" label={t('Manually Track Hours')} form={form} />
          {form.state.data.trackManualHours && (
            <>
              <Label>{t('Hour Meter')}</Label>
              <TextFormField
                className="hour-meter-field"
                type="number"
                name={'hourMeter'}
                placeholder={'0.0'}
                form={form}
                restrictTo={{ maxLength: 10 }}
                validations={[{ validation: v_number_decimals_only }]}
              />
            </>
          )}
        </>
      )}

      <Divider className={'my-4'} />

      <Label>{t('Hourly Cost')}</Label>
      <div className="mb-2">
        {t(
          'The cost to run the item for one hour, minus labor costs. This should be the total of all non-operator costs such as fuel, maintanence, etc.'
        )}
      </div>
      <CurrencyFormField
        className="hour-meter-field"
        name="hourlyCost"
        form={form}
        textAlign="right"
        placeholder="0.00"
        onBlur={(value) => {
          form.setData('hourlyCost', convertStringToSafeNumber(value).toFixed(2));
        }}
      />
      <Label>{t('Effective Date')}</Label>
      <DatePickerFormField
        name="hourlyCostEffectiveDate"
        form={form}
        isOpen={dateOpenable.isOpen}
        onClose={dateOpenable.close}
        onOpen={dateOpenable.open}
      />
      {isEquipmentInspectionsEnabled && (
        <FormAccordion title={t('Inspections')} form={form}>
          <DetailHeaderWithText
            title={t('Pre-Operation Inspection')}
            value={t('Required each time an employee begins using the equipment.')}
          />
          <EquipmentInspectionChecklistPickerFormField name="preOperationChecklistId" form={form} />
          <DetailHeaderWithText
            title={t('Post-Operation Inspection')}
            value={t('Required each time an employee stops using the equipment.')}
          />
          <EquipmentInspectionChecklistPickerFormField name="postOperationChecklistId" form={form} />

          <DetailHeaderWithText
            title={t('Routine Inspections')}
            value={t(
              'Create an inspection reminder after a certain number of days following the previous inspection. Useful for maintenance and other periodic inspections.'
            )}
          />
          <Row justify={Justify.SPACE_BETWEEN}>
            <Label>{t('Inspection')}</Label>
            <Label>{t('Interval (Days)')}</Label>
          </Row>
          <Divider className={'mb-4'} />
          <Row justify={Justify.SPACE_BETWEEN}>
            <EquipmentInspectionChecklistPickerFormField
              name="routineInspectionChecklistId1"
              form={form}
              className={'routine-checklist-picker'}
            />
            <TextFormField name="routineInspectionInterval1" form={form} className={'routine-interval-field'} />
          </Row>
          <Row justify={Justify.SPACE_BETWEEN}>
            <EquipmentInspectionChecklistPickerFormField
              name="routineInspectionChecklistId2"
              form={form}
              className={'routine-checklist-picker'}
            />
            <TextFormField name="routineInspectionInterval2" form={form} className={'routine-interval-field'} />
          </Row>
          <Row justify={Justify.SPACE_BETWEEN}>
            <EquipmentInspectionChecklistPickerFormField
              name="routineInspectionChecklistId3"
              form={form}
              className={'routine-checklist-picker'}
            />
            <TextFormField name="routineInspectionInterval3" form={form} className={'routine-interval-field'} />
          </Row>
        </FormAccordion>
      )}
      {customFields.length > 0 && (
        <FormAccordion title={t('Custom Fields')} form={form}>
          {customFields.map((field, index) => {
            return (
              <React.Fragment key={index}>
                {CustomFormField(form, field, index, isAdmin(activeMember.position))}
              </React.Fragment>
            );
          })}
        </FormAccordion>
      )}

      <Button className="pl-5 pr-5" type="primary" onClick={form.handleSubmit}>
        {t('Create')}
      </Button>
    </div>
  );

  return (
    <>
      <Form className={classes} render={renderFormFields} onSubmit={onSubmitForm} />
      <Loader isOpen={loaderOpenable.isOpen} />
      <ManageEquipmentCategoriesDialog isOpen={manageCategoriesOpenable.isOpen} onClose={handleCategoriesDialogClose} />
      <ManageEquipmentMakesDialog isOpen={manageMakesOpenable.isOpen} onClose={manageMakesOpenable.close} />
      <EquipmentTypesDialog isOpen={typesDialogOpenable.isOpen} onClose={typesDialogOpenable.close} />
    </>
  );
};

export default EquipmentCreateForm;
